/**
 * Deep-link-after-login coordination.
 *
 * Anonymous visit to a protected route → ProtectedRoute redirects to /login
 * with the original `pathname + search` carried in router state. LoginPage
 * stashes that target into sessionStorage before bouncing the user out to
 * Entra (the OAuth round-trip drops React Router state). After Entra
 * redirects back to /auth/callback and the token exchange succeeds,
 * AuthCallbackPage consumes the stash and navigates the user to the
 * original target instead of the role default (/dashboard or /).
 *
 * Per-tab sessionStorage is intentional and **different** from where the
 * access token lives (localStorage, see services/auth.service.ts). The
 * asymmetry is by design:
 *
 *   - Access token → localStorage: "is this user logged in?" is a global
 *     per-user fact; logging in in one tab should sign you in everywhere.
 *   - postLoginRedirect → sessionStorage: "where was *this tab* going when
 *     login interrupted it?" is a tab-local, one-shot piece of routing
 *     state. If it lived in localStorage, opening login in a second tab
 *     mid-flow would overwrite the first tab's deep-link target, and a
 *     stash left over from a closed browser would silently redirect the
 *     next login somewhere unexpected.
 *
 * One concurrent login per tab.
 */

const STORAGE_KEY = 'postLoginRedirect'

/**
 * Persist the post-login navigation target for the current tab. No-op if
 * the path is unsafe (must start with `/`, must not be a protocol-relative
 * URL like `//evil.com`) — defends against open-redirect attacks where an
 * attacker stuffs a foreign URL into router state.
 */
export function stashPostLoginRedirect(target: string): void {
	if (!isSafeRelativePath(target)) return
	sessionStorage.setItem(STORAGE_KEY, target)
}

/**
 * Read and clear the stashed target. Returns `null` if no stash exists or
 * the stashed value failed the safety check (defense in depth — even if a
 * stash got past the writer).
 */
export function consumePostLoginRedirect(): string | null {
	const target = sessionStorage.getItem(STORAGE_KEY)
	sessionStorage.removeItem(STORAGE_KEY)
	if (!target) return null
	return isSafeRelativePath(target) ? target : null
}

function isSafeRelativePath(value: string): boolean {
	// Must be a same-origin path, not an absolute URL or protocol-relative URL.
	if (!value.startsWith('/')) return false
	if (value.startsWith('//')) return false
	return true
}
